/**
 * Admin — 统一汇率（EP 平价 / 通告 / TP / 燃料）
 */
import { useEffect } from 'react';
import { Alert, Button, Card, Form, InputNumber, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface UnifiedPricing {
  ep_usdt: string;
  announce_tp_buy_usdt: string;
  announce_rp_buy_usdt: string;
  announce_ep_sell_usdt: string;
  announce_mint_usdt: string;
  tp_exchange_rate: string;
  fc_usdt_per_fc: string;
}

export function UnifiedPricingPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit =
    hasNode('system.config.edit') ||
    hasNode('system.config') ||
    hasNode('system.config.list') ||
    hasNode('platform.unified_pricing');
  const [form] = Form.useForm();

  const query = useQuery({
    queryKey: ['admin', 'unified-pricing'],
    queryFn: () => api.get<UnifiedPricing>('/api/admin/unified-pricing/settings'),
  });

  useEffect(() => {
    if (query.data) form.setFieldsValue(query.data);
  }, [query.data, form]);

  const save = useMutation({
    mutationFn: (body: Partial<UnifiedPricing>) =>
      api.put<UnifiedPricing>('/api/admin/unified-pricing/settings', body),
    onSuccess: (data) => {
      message.success('统一汇率已保存');
      form.setFieldsValue(data);
      qc.setQueryData(['admin', 'unified-pricing'], data);
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '保存失败'),
  });

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        统一汇率
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="配置 EP 交易平价、汇率通告文案数字、TP 充值汇率、燃料 FC 汇率。无等级档位。"
      />
      <Card
        loading={query.isLoading}
        extra={
          canEdit ? (
            <Button type="primary" loading={save.isPending} onClick={() => form.submit()}>
              保存
            </Button>
          ) : null
        }
      >
        <Form
          form={form}
          layout="vertical"
          disabled={!canEdit}
          onFinish={(v) =>
            save.mutate({
              ep_usdt: String(v.ep_usdt),
              announce_tp_buy_usdt: String(v.announce_tp_buy_usdt),
              announce_rp_buy_usdt: String(v.announce_rp_buy_usdt),
              announce_ep_sell_usdt: String(v.announce_ep_sell_usdt),
              announce_mint_usdt: String(v.announce_mint_usdt),
              tp_exchange_rate: String(v.tp_exchange_rate),
              fc_usdt_per_fc: String(v.fc_usdt_per_fc),
            })
          }
        >
          <Typography.Title level={5}>EP 交易</Typography.Title>
          <Form.Item
            name="ep_usdt"
            label="EP 单价 (USDT / 1 EP)"
            rules={[{ required: true }]}
            extra="设置后覆盖旧版 CNY 档位表"
          >
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT" />
          </Form.Item>

          <Typography.Title level={5}>通告汇率（汇率换算页）</Typography.Title>
          <Form.Item name="announce_tp_buy_usdt" label="进场 TP 买价" rules={[{ required: true }]}>
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT/TP" />
          </Form.Item>
          <Form.Item name="announce_rp_buy_usdt" label="进场 RP 买价" rules={[{ required: true }]}>
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT/RP" />
          </Form.Item>
          <Form.Item name="announce_ep_sell_usdt" label="出场卖价 EP/RP" rules={[{ required: true }]}>
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT" />
          </Form.Item>
          <Form.Item name="announce_mint_usdt" label="铸造兑换价" rules={[{ required: true }]}>
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT" />
          </Form.Item>

          <Typography.Title level={5}>充值汇率</Typography.Title>
          <Form.Item
            name="tp_exchange_rate"
            label="TP 充值汇率 (USDT / 1 TP)"
            rules={[{ required: true }]}
          >
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT/TP" />
          </Form.Item>
          <Form.Item
            name="fc_usdt_per_fc"
            label="燃料 FC 汇率 (USDT / 1 FC)"
            rules={[{ required: true }]}
          >
            <InputNumber min={0.0001} step={0.01} style={{ width: 220 }} addonAfter="USDT/FC" />
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}

export default UnifiedPricingPage;
